import type { ContactFilters as ContactFiltersType } from '@Components/ContactList/types';
import { FIELD_MAPPING } from '@Store/api/contactApiSlice';

export interface FilterCriteria {
  name: string;
  value: string | number;
  operator: string;
}

export interface SearchCriteria {
  searchCriterias: FilterCriteria[];
  dataOperator: string;
}

/**
 * Processes filter values and converts them into API-compatible filter criteria
 * @param filterValues - The filter values from the form
 * @returns Array of processed filter criteria
 */
export const processFilterCriteria = (
  filterValues: ContactFiltersType
): FilterCriteria[] => {
  const filterCriterias: FilterCriteria[] = [];

  Object.entries(filterValues).forEach(([key, value]) => {
    if (value && value !== 'all' && value.trim()) {
      const apiField = FIELD_MAPPING[key] || key;
      let processedValue: string | number = value.trim();

      // Map work location values
      if (key === 'workLocation') {
        if (value === '1') {
          processedValue = 'h'; // DEPARTMENT -> h
        } else if (value === '2') {
          processedValue = 'p'; // PROJECT -> p
        }
      }

      // Convert deptProjId to integer
      if (key === 'department') {
        processedValue = parseInt(value, 10);
      }

      filterCriterias.push({
        name: apiField,
        value: processedValue,
        operator:
          key === 'employeeName' ||
          key === 'occupation' ||
          key === 'employeeNumber'
            ? 'like'
            : 'eq',
      });
    }
  });

  return filterCriterias;
};

/**
 * Creates search criteria list for API request
 * @param searchQuery - The search query string
 * @param filterValues - The filter values from the form
 * @returns Array of search criteria
 */
export const createSearchCriteriaList = (
  searchQuery: string,
  filterValues: ContactFiltersType
): SearchCriteria[] => {
  const searchCriteriaList: SearchCriteria[] = [];

  // Add search value if provided - search across empNumber, empName, workLocation, and deptProjId
  if (searchQuery.trim()) {
    searchCriteriaList.push({
      searchCriterias: [],
      dataOperator: 'and',
    });
  }

  // Add filter criteria
  const filterCriterias = processFilterCriteria(filterValues);

  if (filterCriterias.length > 0) {
    searchCriteriaList.push({
      searchCriterias: filterCriterias,
      dataOperator: 'and',
    });
  }

  return searchCriteriaList;
};
